import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images = [], initialIndex = 0, open, onClose }) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex, open]);

  const prev = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setIndex((i) => (i - 1 + images.length) % images.length);
    },
    [images.length]
  );
  const next = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setIndex((i) => (i + 1) % images.length);
    },
    [images.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, images.length]);

  if (!open) return null;

  // Backdrop click handler — closes only when the click target IS the backdrop
  // (not a descendant such as the image, arrows or thumbnails).
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      data-testid="lightbox"
      onClick={onBackdropClick}
    >
      {/* Top bar — counter + close X */}
      <div className="flex items-center justify-between p-5 text-white" onClick={onBackdropClick}>
        <span className="font-heading text-sm tracking-[0.2em] uppercase pointer-events-none">
          {index + 1} / {images.length}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="w-10 h-10 flex items-center justify-center hover:bg-white/10"
          data-testid="lightbox-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Middle — image area. Click on this DIV (the dark area around image) also closes. */}
      <div
        className="flex-1 relative flex items-center justify-center px-4 md:px-16"
        onClick={onBackdropClick}
      >
        <button
          onClick={prev}
          className="absolute left-2 md:left-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10"
          data-testid="lightbox-prev"
          aria-label="Previous"
        >
          <ChevronLeft className="w-7 h-7" />
        </button>
        <img
          src={images[index]}
          alt=""
          onClick={(e) => e.stopPropagation()}
          className="max-h-[78vh] max-w-full object-contain select-none cursor-default"
        />
        <button
          onClick={next}
          className="absolute right-2 md:right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10"
          data-testid="lightbox-next"
          aria-label="Next"
        >
          <ChevronRight className="w-7 h-7" />
        </button>
      </div>

      {/* Thumbnail strip — outer wrapper closes on click; inner row contains the thumbs */}
      <div className="p-5" onClick={onBackdropClick}>
        <div
          className="flex gap-2 justify-center overflow-x-auto"
          onClick={(e) => {
            // If user clicked the row container itself (gaps between thumbnails),
            // close the lightbox. Clicks on the thumbnail <button>s stop propagation below.
            if (e.target === e.currentTarget) onClose();
          }}
        >
          {images.map((src, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setIndex(i); }}
              className={`relative w-14 h-14 shrink-0 overflow-hidden border-2 transition-colors ${
                i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
              }`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
